<template>
	<div class="animated fadeIn">
    <g-table :table-data="customers" :filter="true" :fields="fields" :per-page=10 hover striped bordered small caption="Office List">
			<template slot="row-actions" slot-scope="row">
				<b-button
					v-if="$store.getters.canWrite"
					active v-b-modal.customer-editor
					size="sm" pill variant="info" class="m-1" @click="passDataToModal(row)">Configure</b-button>
			</template>
		</g-table>

		<b-modal id="customer-editor" title="Edit Customer" size="lg" @ok="saveCustomer" ok-title="Save" :ok-disabled="!$store.getters.canWrite" @cancel="clearModalData">
			<form action="">
				<b-row>
					<b-col md="6">
						<b-form-group label="Name" label-for="customerName">
							<b-form-input name="name" id="customerName" v-validate="{required:true}" :state="!errors.has('name')" v-model="customerToBeEdited.name" trim></b-form-input>
							<b-form-invalid-feedback v-show="errors.has('name')">{{errors.first('name')}}</b-form-invalid-feedback>
						</b-form-group>
						<b-form-group label="Client Panel Title" label-for="clientPanelTitle">
							<b-form-input v-model="customerToBeEdited.clientPanelTitle"
								v-validate="{required: true,min:1}"
								name="clientPanelTitle" :state="!errors.has('clientPanelTitle')"
								type="text" placeholder="Enter Client Panel Title"></b-form-input>
							<b-form-invalid-feedback v-show="errors.has('clientPanelTitle')">{{errors.first('clientPanelTitle')}}</b-form-invalid-feedback>
						</b-form-group>
						<b-form-group label="Email" label-for="mail">
							<b-form-input v-model="customerToBeEdited.mail" v-validate="{email:true}" name="mail" :state="!errors.has('mail')" aria-describedby="mail-errors" type="text" placeholder="Enter email"></b-form-input>
							<b-form-invalid-feedback v-show="errors.has('mail')">{{errors.first('mail')}}</b-form-invalid-feedback>
						</b-form-group>
						<b-form-group label="Location" label-for="location">
							<b-form-input name="location" v-model="customerToBeEdited.location" id="location" type="text" placeholder="Enter Location"></b-form-input>
						</b-form-group>
						<b-form-group label="Sales Channel" label-for="channel">
							<b-form-select v-model="customerToBeEdited.channelId">
								<option v-for="channel in channelList" :value="channel.value" :key="channel.value">
									{{ channel.text }}
								</option>
							</b-form-select>
						</b-form-group>
						<b-form-group label="Version" label-for="version">
							<b-form-select v-model="customerToBeEdited.versionId">
								<option v-for="version in versionList" :value="version.id" :key="version.id">
									{{ version.name }}
								</option>
							</b-form-select>
						</b-form-group>
						<b-form-group label="Send Mail" label-for="sendMail">
							<b-form-select v-model="customerToBeEdited.sendMail">
								<option value="true">Yes</option>
								<option value="false">No</option>
							</b-form-select>
						</b-form-group>
					</b-col>
					<b-col md="6">
						<b-form-group
							label="TipsoiPay ID"
							label-for="billingProjectId"
							description="Tipsoi Pay billing project ID. Leave blank to unlink. Needs the Client ID below to fully link billing sync.">
							<b-form-input name="billingProjectId" v-model="customerToBeEdited.billingProjectId" id="billingProjectId" type="text" placeholder="e.g. 5678"></b-form-input>
						</b-form-group>
						<b-form-group
							label="TipsoiPay Client ID"
							label-for="billingClientId"
							description="Tipsoi Pay billing contact/client ID (pairs with the project ID for billing sync).">
							<b-form-input name="billingClientId" v-model="customerToBeEdited.billingClientId" id="billingClientId" type="text" placeholder="e.g. 1234"></b-form-input>
						</b-form-group>
						<b-form-group label="Login Enabled">
							<toggle-button v-model="customerToBeEdited.loginEnabled"/>
						</b-form-group>
						<b-form-group label="Has API">
							<toggle-button @change="updateApiAccess" v-model="customerToBeEdited.hasApiAccess"/>
						</b-form-group>
						<b-form-group label="Api Token" label-for="apiToken">
							<p class="mb-0 text-break small">{{ customerToBeEdited.apiToken }}</p>
						</b-form-group>
						<b-form-group label="Send Token To" label-for="mailTokenTo">
							<b-form-input v-model="mailTokenTo" id="mailTokenTo" type="text" placeholder="Enter email"></b-form-input>
						</b-form-group>
						<div class="d-flex flex-wrap justify-content-end">
							<b-button v-if="$store.getters.canWrite" size="sm" @click="generateNewToken" variant="info" class="m-1">Generate New Token</b-button>
							<b-button v-if="$store.getters.canWrite" size="sm" @click="mailToken" variant="info" class="m-1">Mail Token</b-button>
						</div>
					</b-col>
				</b-row>
				<hr class="my-3">
				<b-row>
					<b-col md="4">
						<b-form-group label="Primary Logo">
							<div v-show="showPrimaryLogoCropper" class="mb-2">
								<cropper
									ref="primaryLogoCropper"
									:canvas="{
										minHeight: 0,
										minWidth: 0,
										maxHeight: 64,
										maxWidth: 206,
									}"
									:src="officeLogo.src"
								/>
							</div>
							<div v-if="!showPrimaryLogoCropper && customerToBeEdited.logoUrl != null && customerToBeEdited.logoUrl !== ''" class="mb-2">
								<b-img fluid thumbnail :src="customerToBeEdited.logoUrl" />
							</div>
							<b-form-file size="sm" accept="image/png" @change="loadImage($event, 0)"></b-form-file>
						</b-form-group>
					</b-col>
					<b-col md="4">
						<b-form-group label="Additional Logo 1">
							<div v-show="showAdditionalLogo1Cropper" class="mb-2">
								<cropper
									ref="additionalLogo1Cropper"
									:canvas="{
										minHeight: 0,
										minWidth: 0,
										maxHeight: 64,
										maxWidth: 206,
									}"
									:src="additionalLogo1.src"
								/>
							</div>
							<div v-if="!showAdditionalLogo1Cropper && customerToBeEdited.logoUrl2 != null && customerToBeEdited.logoUrl2 !== ''" class="mb-2">
								<b-img fluid thumbnail :src="customerToBeEdited.logoUrl2" />
							</div>
							<b-form-file size="sm" accept="image/png" @change="loadImage($event, 1)"></b-form-file>
						</b-form-group>
					</b-col>
					<b-col md="4">
						<b-form-group label="Additional Logo 2">
							<div v-show="showAdditionalLogo2Cropper" class="mb-2">
								<cropper
									ref="additionalLogo2Cropper"
									:canvas="{
										minHeight: 0,
										minWidth: 0,
										maxHeight: 64,
										maxWidth: 206,
									}"
									:src="additionalLogo2.src"
								/>
							</div>
							<div v-if="!showAdditionalLogo2Cropper && customerToBeEdited.logoUrl3 != null && customerToBeEdited.logoUrl3 !== ''" class="mb-2">
								<b-img fluid thumbnail :src="customerToBeEdited.logoUrl3" />
							</div>
							<b-form-file size="sm" accept="image/png" @change="loadImage($event, 2)"></b-form-file>
						</b-form-group>
					</b-col>
				</b-row>
			</form>
		</b-modal>
	</div>
</template>

<script>
import versionService from '@/services/versionService'
import customerService from '@/services/customerService'
import gTable from '@/components/GenericTable'
import { ToggleButton } from 'vue-js-toggle-button'
import toastService from "@/services/toastService";
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css';

function getMimeType(file, fallback = null) {
	const byteArray = (new Uint8Array(file)).subarray(0, 4);
	let header = '';
	for (let i = 0; i < byteArray.length; i++) {
		header += byteArray[i].toString(16);
	}
	switch (header) {
		case "89504e47":
			return "image/png";
		case "47494638":
			return "image/gif";
		case "ffd8ffe0":
		case "ffd8ffe1":
		case "ffd8ffe2":
		case "ffd8ffe3":
		case "ffd8ffe8":
			return "image/jpeg";
		default:
			return fallback;
	}
}

export default {
  name: 'list',
  components: {gTable,ToggleButton, Cropper},
  data: () => {
    return {
			customers:[],
			versionList:[],
			channelList:[],
			customerToBeEdited:{},
			mailTokenTo:'',
      fields                     : [
        {key: 'id', label: 'ID', sortable: true},
        {key: 'name', label: 'Name', sortable: true,searchable:true},
        {key: 'clientPanelTitle', label: 'CP Name', sortable: true},
        {key: 'mail', label: 'Email', sortable: true},
        {key: 'location', label: 'Location', sortable: true},
        {key: 'billingProjectId', label: 'TipsoiPay ID', sortable: true,
          formatter: value => (value === null || value === undefined || value === '') ? '—' : value},
        {key: 'version', label: 'Version', sortable: true},
				{key: 'sendMail', label: 'Send Mail', sortable: true},
				{key: 'status', label: 'Status', sortable: true},
		{key: 'channelId', label: 'Sales Channel', sortable: true},
		{key: 'billingInvoiceUrl', label: 'Invoice', sortable: false},
        {key: 'actions', label: 'Actions'},
				{key: 'loginEnabled', label: "Login Enabled"}
      ],
			showPrimaryLogoCropper     : false,
			showAdditionalLogo1Cropper : false,
			showAdditionalLogo2Cropper : false,
			officeLogo                 : {
				src: null,
				fileName: null,
				type: null
			},
			additionalLogo1            : {
				src: null,
				fileName: null,
				type: null
			},
			additionalLogo2             : {
				src: null,
				fileName: null,
				type: null
			}
    }
  },
	methods : {
  	mailToken(){
  		if(this.mailTokenTo.length == 0){
  			toastService.showErrorToast("Please specify mail address");
			}
  		let request = {
				id:this.customerToBeEdited.id,
				email: this.mailTokenTo
			};
			customerService.mailApiToken(request,data=>{
				toastService.showSuccessToast("Mail sent");
			}, error => {
				toastService.showErrorToast("Could not send mail" +error)
			});
		},
		generateNewToken(){
			customerService.generateNewToken(this.customerToBeEdited.id,data => {
				this.customerToBeEdited.apiToken = data.apiToken;
			},error =>{
				toastService.showErrorToast("Could not generate token " + error);
			});
		},
  	updateApiAccess(){
			let request = {
				id:this.customerToBeEdited.id,
				value : this.customerToBeEdited.hasApiAccess
			};
			customerService.updateApiAccess(request,data => {
				this.customerToBeEdited.hasApiAccess = data.hasApiAccess;
			}, error =>{
				toastService.showErrorToast(error)
			});
		},
  	saveCustomer(e){
			if(this.errors.all().length>0){
				e.preventDefault();
			}

			let primaryLogoImgUrl = this.$refs.primaryLogoCropper.getResult().canvas ? this.$refs.primaryLogoCropper.getResult().canvas.toDataURL() : null;
			let additionalLogo1ImgUrl = this.$refs.additionalLogo1Cropper.getResult().canvas ? this.$refs.additionalLogo1Cropper.getResult().canvas.toDataURL() : null;
			let additionalLogo2ImgUrl = this.$refs.additionalLogo2Cropper.getResult().canvas ? this.$refs.additionalLogo2Cropper.getResult().canvas.toDataURL() : null;

			// let { canvas } = this.$refs.primaryLogoCropper.getResult();
			// let form = new FormData();
		let request = {
			// id : this.customerToBeEdited.id,
			name : this.customerToBeEdited.name,
			clientPanelTitle : this.customerToBeEdited.clientPanelTitle,
			timezone : this.customerToBeEdited.timezone,
			email : this.customerToBeEdited.mail,
			location : this.customerToBeEdited.location,
			versionId : this.customerToBeEdited.versionId,
			sendMail : this.customerToBeEdited.sendMail,
			primaryLogoImgUrl : primaryLogoImgUrl,
			additionalLogo1ImgUrl : additionalLogo1ImgUrl,
			additionalLogo2ImgUrl : additionalLogo2ImgUrl,
			loginEnabled : this.customerToBeEdited.loginEnabled,
			channelId: this.customerToBeEdited.channelId,
			billingProjectId : this.customerToBeEdited.billingProjectId,
			billingClientId : this.customerToBeEdited.billingClientId
		};
		// console.log(request)
			// form.append('postBody', JSON.stringify(request));

			// if(canvas) {
			// 	this.getCroppedImageFile(canvas.toDataURL()).then(croppedImgFile => {
			// 		this.customerToBeEdited.logo = croppedImgFile;
			// 		form.append('logo', this.customerToBeEdited.logo);
			// 		this.submitCustomerEditForm(form);
			// 	});
			// } else {
			// 	this.submitCustomerEditForm(form);
			// }
			this.submitCustomerEditForm(request);
		},
		getCroppedImageFile(imgDataUrl) {
			return fetch(imgDataUrl)
				.then(res => res.blob())
				.then(blob => {
					return new File([blob], this.officeLogo.fileName,{ type: this.officeLogo.type });
				});
		},
		// submitCustomerEditForm(form) {
		// 	customerService.updateCustomer(this.customerToBeEdited.id, form, data => {
		// 		this.customers = data;
		// 		toastService.showSuccessToast("Customer updated");
		// 	},error=>{
		// 		toastService.showErrorToast(error);
		// 	});
		// },
		submitCustomerEditForm(request) {
			customerService.updateCustomer(this.customerToBeEdited.id, request, data => {
				this.customers = data;
				this.getCustomerList();
				toastService.showSuccessToast("Customer updated");
			},error=>{
				toastService.showErrorToast(error);
			});
		},
		getVersionList(){
  		versionService.getVersionList(data=>{
  			this.versionList = data;
			},error=>{
				toastService.showErrorToast(error);
			});
		},
		getSalesChannelList(){
			this.channelList = [
					{text:'Please Select', value:null},
					{text: 'Upsell', value:'Upsell'},
					{text:'Telesales', value:'Telesales'},
					{text:'Reseller Partners', value:'Reseller Partners'},
					{text:'Enterprise', value:'Enterprise'},
					{text:'Distributors', value:'Distributors'},
					{text:'Test', value:'Test'},
					{text:'Integration Partners', value:'Integration Partners'},
					{text:'International', value:'International'},
					{text:'Partner-led Sales', value:'Partner-led Sales'}
				];
		},
		getCustomerList() {
			customerService.getCustomerList(data => {
				this.customers = data.map( customer =>{
					delete customer.version.modules;
					return customer;
				});
			}, error => {
				toastService.showErrorToast(error);
			});
		},
		passDataToModal(customer) {
  		this.showPrimaryLogoCropper = false;
			this.showAdditionalLogo1Cropper = false;
			this.showAdditionalLogo2Cropper = false;
			this.customerToBeEdited = JSON.parse(JSON.stringify(customer.row.item));
			this.customerToBeEdited.versionId = this.customerToBeEdited.version.id;
			this.customerToBeEdited.logo = null;
			this.mailTokenTo = this.customerToBeEdited.mail;
		},
		clearModalData() {
			this.customerToBeEdited = {};
			this.mailTokenTo = '';
			this.destroyed();
			this.nullifyLogoVariables();
		},
		loadImage(event, targetLogoIndex) {
			const { files } = event.target;
			if (files && files[0]) {

				if(targetLogoIndex === 0) {
					if (this.officeLogo.src) {
						URL.revokeObjectURL(this.officeLogo.src);
					}
				} else if(targetLogoIndex === 1) {
						if (this.additionalLogo1.src) {
							URL.revokeObjectURL(this.additionalLogo1.src);
						}
				} else if(targetLogoIndex === 2) {
						if (this.additionalLogo2.src) {
							URL.revokeObjectURL(this.additionalLogo2.src);
						}
				}
				const blob = URL.createObjectURL(files[0]);

				const reader = new FileReader();
				reader.onload = (e) => {
					this.assignImageData(e, blob, files[0], targetLogoIndex);
				};
				reader.readAsArrayBuffer(files[0]);
			}
		},
		assignImageData(event, blob, file, targetLogoIndex) {
			if(targetLogoIndex === 0) {
					this.officeLogo = {
						src: blob,
						fileName: file.name,
						type: getMimeType(event.target.result, file.type)
					};
					this.showPrimaryLogoCropper = true;
			} else if(targetLogoIndex === 1) {
					this.additionalLogo1 = {
						src: blob,
						fileName: file.name,
						type: getMimeType(event.target.result, file.type)
					};
					this.showAdditionalLogo1Cropper = true;
			} else if(targetLogoIndex === 2) {
					this.additionalLogo2 = {
						src: blob,
						fileName: file.name,
						type: getMimeType(event.target.result, file.type)
					};
					this.showAdditionalLogo2Cropper = true;
			}
		},
		destroyed() {
			// Revoke the object URL, to allow the garbage collector to destroy the uploaded before file
			if (this.officeLogo.src) {
				URL.revokeObjectURL(this.officeLogo.src)
			}
			if (this.additionalLogo1.src) {
				URL.revokeObjectURL(this.additionalLogo1.src)
			}
			if (this.additionalLogo2.src) {
				URL.revokeObjectURL(this.additionalLogo2.src)
			}
		},
		nullifyLogoVariables() {
			this.officeLogo = {
				src: null,
				fileName: null,
				type: null
			};
			this.additionalLogo1 = {
				src: null,
				fileName: null,
				type: null
			};
			this.additionalLogo2 = {
				src: null,
				fileName: null,
				type: null
			}
		}
	},
	mounted(){
  	this.getCustomerList();
  	this.getVersionList();
	this.getSalesChannelList();
	}
}
</script>
